import { useMemo, useCallback, useState } from 'react'
import { PersonalityDatum } from '@crystal-eyes/types'
import { gql, queryWithAuth } from '@crystal-eyes/utils/apis/graphqlApi'
import useUserIdentity from '@crystal-eyes/hooks/user/useUserIdentity'
import useAuth from '@crystal-eyes/hooks/useAuth'
import useSWR from 'swr'

interface Identity {
  id: string
}

const ENDORSE = gql`
  mutation Endorse($datumIds: [Guid!]) {
    endorseDatums(ids: $datumIds) {
      id
    }
  }
`

const UNENDORSE = gql`
  mutation Unendorse($datumIds: [Guid!]) {
    unendorseDatums(ids: $datumIds) {
      id
    }
  }
`

type Options = {
  datum: PersonalityDatum
}

type State = {
  data: { didEndorse: boolean; endorsedBy: Identity[] }
  loading: boolean
  error: any
  doEndorse: () => void
  doUnendorse: () => void
}

type EndorseState =
  | { state: 'not_loading' }
  | { state: 'loading' }
  | { state: 'error'; error: any }

export default function useEndorsements({ datum }: Options): State {
  const { data: auth } = useAuth()
  const { data: userIdentity } = useUserIdentity()
  const [endorseState, setEndorseState] = useState<EndorseState>({
    state: 'not_loading',
  })
  const [unendorseState, setUnendorseState] = useState<EndorseState>({
    state: 'not_loading',
  })

  const fetcher = () => datum?.endorsements

  const { data: endorsements, mutate } = useSWR(
    auth ? ['useEndorsements', datum.id] : null,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )

  const doEndorse = useCallback(() => {
    setEndorseState({ state: 'loading' })
    queryWithAuth(auth, ENDORSE, { datumIds: [datum.id] })
      .then(() => {
        mutate([...(endorsements || []), userIdentity], { revalidate: false })
        setEndorseState({ state: 'not_loading' })
      })
      .catch(err => {
        setEndorseState({ state: 'error', error: err })
      })
  }, [auth, endorsements, userIdentity])

  const doUnendorse = useCallback(() => {
    setUnendorseState({ state: 'loading' })
    queryWithAuth(auth, UNENDORSE, { datumIds: [datum.id] })
      .then(() => {
        mutate(
          (endorsements || []).filter(ident => ident.id !== userIdentity?.id),
          { revalidate: false },
        )
        setUnendorseState({ state: 'not_loading' })
      })
      .catch(err => {
        setUnendorseState({ state: 'error', error: err })
      })
  }, [auth, endorsements, userIdentity])

  const didEndorse = useMemo(() => {
    return !!endorsements?.find(ident => ident.id === userIdentity?.id)
  }, [endorsements, userIdentity])

  return {
    data: { didEndorse, endorsedBy: endorsements || [] },
    loading:
      endorseState.state === 'loading' || unendorseState.state === 'loading',
    error:
      (endorseState.state === 'error' && endorseState.error) ||
      (unendorseState.state === 'error' && unendorseState.error),
    doEndorse,
    doUnendorse,
  }
}
